/**
 * Admin Home — "Faculty Console"
 * Compact operational indicators + quick actions.
 * Everything wire-reads existing endpoints hydrated by AdminPortal.
 */
import React from 'react';
import {
  ClipboardCheck, Package, Undo2, AlertOctagon, ChevronRight,
} from 'lucide-react';

const IND = ({ label, value, hint, tone = 'ok', testId }) => (
  <div className="ap-ind" data-testid={testId}>
    <div className="ap-ind-label">
      <em className={tone === 'warn' ? 'is-warn' : tone === 'exc' ? 'is-exc' : ''} />
      {label}
    </div>
    <div className="ap-ind-value">{value}</div>
    <div className="ap-ind-hint">{hint}</div>
  </div>
);

const Quick = ({ icon: Icon, title, desc, onClick, testId }) => (
  <button type="button" className="ap-quick" onClick={onClick} data-testid={testId}>
    <span className="ap-quick-icon"><Icon size={18} strokeWidth={1.7} /></span>
    <div className="ap-quick-title">{title}</div>
    <div className="ap-quick-desc">{desc}</div>
    <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--diis-accent-ink)', fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      Open <ChevronRight size={13} />
    </div>
  </button>
);

export default function HomePage({ ctx }) {
  const { counts, goto, user } = ctx;
  const firstName = (user?.full_name || user?.username || '').split(' ')[0] || (user?.username || '');

  return (
    <div data-testid="ap-home">
      <div className="ap-eyebrow">Faculty Console</div>
      <h1 className="ap-page-title">Good day, {firstName}.</h1>
      <p className="ap-page-lede">
        Review requests directed to you, keep an eye on outstanding returnables, and
        act on low-stock signals — all rooted in real-time inventory data.
      </p>

      <div className="ap-ind-row" role="group" aria-label="Operational indicators">
        <IND
          label="Pending Requests"
          value={counts.pending}
          hint="Awaiting your approval"
          tone="ok"
          testId="ap-ind-pending"
        />
        <IND
          label="Overdue Returnables"
          value={counts.overdue}
          hint={counts.overdue === 1 ? 'Component past its return date' : 'Components past their return date'}
          tone="exc"
          testId="ap-ind-overdue"
        />
        <IND
          label="Defaulter Students"
          value={counts.defaulters}
          hint="Students with outstanding overdue items"
          tone="exc"
          testId="ap-ind-defaulters"
        />
        <IND
          label="Low Stock Items"
          value={counts.lowStock}
          hint="At or below threshold"
          tone="warn"
          testId="ap-ind-low-stock"
        />
      </div>

      <div className="ap-quick-grid">
        <Quick
          icon={ClipboardCheck}
          title="Review Requests"
          desc="Approve, set a return timeline, or reject the requests directed to you."
          onClick={() => goto('requests')}
          testId="ap-quick-requests"
        />
        <Quick
          icon={Package}
          title="Inventory"
          desc="Manage components, bulk upload, and older-stock reconciliation."
          onClick={() => goto('inventory')}
          testId="ap-quick-inventory"
        />
        <Quick
          icon={Undo2}
          title="Returns"
          desc="Give final approval to clerk-verified returns and credit stock back."
          onClick={() => goto('returns')}
          testId="ap-quick-returns"
        />
      </div>

      {counts.overdue > 0 && (
        <div className="ap-mt-6" style={{ maxWidth: 720 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', border: '1px solid rgba(176, 74, 60, 0.22)', background: 'rgba(176, 74, 60, 0.06)', borderRadius: 'var(--diis-radius)', color: '#6B2E23' }} data-testid="ap-home-overdue-nudge">
            <AlertOctagon size={18} style={{ color: '#B04A3C', flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              <strong style={{ fontWeight: 600 }}>{counts.overdue} outstanding overdue item{counts.overdue === 1 ? '' : 's'}</strong> across{' '}
              <strong style={{ fontWeight: 600 }}>{counts.defaulters}</strong> student{counts.defaulters === 1 ? '' : 's'}. Each pending request card surfaces this context — you can still approve, this is a soft signal only.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
